import React, { useEffect, useState } from "react";
import { Truck, Eye, Send, ClipboardCheck, Printer, DollarSign, Archive, List, Layers, ListChecks, RefreshCw, Undo2 } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { JobDetailModal } from "../components/JobDetailModal";
import { DispatchCloseoutModal, openCustomerFile } from "../components/DispatchCloseoutModal";
import { MarkPaidModal } from "../components/MarkPaidModal";
import { InvoiceCaptureModal } from "../components/InvoiceCaptureModal";
import { InlineInvoiceEdit } from "../components/InlineInvoiceEdit";
import { InlinePoDateEdit } from "../components/InlinePoDateEdit";
import { InlineQuoteEdit } from "../components/InlineQuoteEdit";
import { SendBackToLabModal, SEND_BACK_TO_LAB_STATUSES } from "../components/SendBackToLabModal";
import { DispatchByJobView } from "./DispatchByJobView";
import { StatusBadge, PaymentBadge, CloseHints, STATUS } from "../components/Badges";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { fmtNum } from "../lib/format";

const TABS = [
  ["ready", "In Dispatch", Send],      // status: ready_for_delivery
  ["dispatched", "Ready for Collection", Truck],
  ["awaiting_payment", "Awaiting payment", DollarSign],
  ["closed", "Closed", Archive],
];

// SPT-PTE-2026-0062 follow-up — see matching note in DispatchByJobView.js:
// only offer a "Move forward" / "Payment" target that's genuinely forward
// from the tab currently being viewed (not the item's own current status
// again, and not "Ready for Admin" which is actually a step BACKWARDS).
const FORWARD_TARGETS_BY_TAB = {
  ready: ["dispatched"],
  dispatched: [],
  awaiting_payment: [],
  closed: [],
};
const PAYMENT_TARGETS_BY_TAB = {
  ready: ["awaiting_payment", "paid"],
  dispatched: ["awaiting_payment", "paid"],
  awaiting_payment: ["paid"],
  closed: [],
};
const statusLabel = (s) => STATUS[s]?.[0] || s.replace(/_/g, " ");
const PAYMENT_LABEL = { awaiting_payment: "Awaiting Payment", paid: "Mark as Paid" };

function DispatchBulkBar({ selectedIds, sourceStatus, clearSelection, onDone }) {
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const apply = async () => {
    if (!target) return toast.error("Pick a target status first.");
    const label = statusLabel(target);
    if (!window.confirm(`Move ${selectedIds.length} item(s) to "${label}"?`)) return;
    setBusy(true);
    try {
      const r = (target === "awaiting_payment" || target === "paid")
        ? await api.post("/job-items/bulk-payment", { item_ids: selectedIds, payment_status: target, note: notes || null })
        : await api.post("/job-items/bulk-status", { item_ids: selectedIds, status: target, notes: notes || null });
      const skipped = (r.data.skipped || []).length;
      const pending = r.data.pending_dispatch_count || 0;
      if (pending) {
        toast.warning(`${r.data.count} marked paid · ${pending} not yet dispatched — they'll stay in "In Dispatch" until handed over, then close automatically`, { duration: 8000 });
      } else if (skipped) toast.warning(`${r.data.count} updated · ${skipped} skipped (see audit log)`);
      else toast.success(`${r.data.count} item(s) moved to "${label}"`);
      clearSelection();
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="dispatch-bulk-status">
        <option value="">— Pick target status —</option>
        {FORWARD_TARGETS_BY_TAB[sourceStatus]?.length > 0 && (
          <optgroup label="Move forward">
            {FORWARD_TARGETS_BY_TAB[sourceStatus].map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </optgroup>
        )}
        {PAYMENT_TARGETS_BY_TAB[sourceStatus]?.length > 0 && (
          <optgroup label="Payment">
            {PAYMENT_TARGETS_BY_TAB[sourceStatus].map((s) => <option key={s} value={s}>{PAYMENT_LABEL[s]}</option>)}
          </optgroup>
        )}
        <optgroup label="Send back to Lab">
          {SEND_BACK_TO_LAB_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </optgroup>
      </select>
      <input className="input" placeholder="Optional note (audited)" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="dispatch-bulk-note"
              style={{ minWidth: 200, flex: 1 }} />
      <button className="btn gold sm" onClick={apply} disabled={busy || !target}
              data-testid="dispatch-bulk-apply">
        {busy ? <><RefreshCw size={13} className="spin" /> Applying…</> : <><ListChecks size={13} /> Apply to {selectedIds.length}</>}
      </button>
      <button className="btn ghost sm" onClick={() => setInvoiceOpen(true)} disabled={busy}
              data-testid="dispatch-bulk-invoice">
        <DollarSign size={13} /> Capture Invoice / Awaiting Payment
      </button>
      {invoiceOpen && (
        <InvoiceCaptureModal itemIds={selectedIds}
          subtitle={`${selectedIds.length} item(s) selected`}
          onClose={() => setInvoiceOpen(false)}
          onDone={() => { clearSelection(); onDone(); }} />
      )}
    </>
  );
}

export default function Dispatch() {
  const { can } = useAuth();
  const canEdit = can("dispatch", "edit");
  const [tab, setTab] = useState("ready");
  const [view, setView] = useState("byjob"); // "equipment" | "byjob"
  const [refresh, setRefresh] = useState(0);
  const [closeoutJob, setCloseoutJob] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [markingPaidRow, setMarkingPaidRow] = useState(null);
  const [sendingBackRow, setSendingBackRow] = useState(null);
  const [invoicingRow, setInvoicingRow] = useState(null);
  const [tabCounts, setTabCounts] = useState({});
  const [apSummary, setApSummary] = useState({ count: 0, total_amount: 0, oldest_days: null });
  const [showLegacy, setShowLegacy] = useState(false);
  const includeLegacy = tab === "closed" && showLegacy;

  useEffect(() => {
    api.get("/dispatch/status-counts", { params: { hide_paid: true } }).then((r) => {
      setTabCounts(r.data.counts || {});
      if (r.data.awaiting_payment_summary) setApSummary(r.data.awaiting_payment_summary);
    }).catch(() => {});
  }, [refresh]);

  const columns = [
    { key: "delivery_note_number", label: "DN", sortable: true, mono: true, render: (v) => <span className="t-strong mono">{v || "—"}</span> },
    { key: "description", label: "Equipment", sortable: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "serial_number", label: "Serial #", mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "issue_number", label: "Issue #", mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "customer_name", label: "Customer", sortable: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "status", label: "Status", sortable: true, render: (v, row) => <StatusBadge status={row.quote_declined ? "quote_declined" : v} /> },
    { key: "payment_status", label: "Payment", sortable: true, render: (v) => <PaymentBadge status={v} /> },
    { key: "to_close", label: "To close", render: (_, row) => (
        <CloseHints status={row.status} paymentStatus={row.payment_status}
          quote={row.quote_number} po={row.purchase_order} invoice={row.invoice_number}
          testidPrefix={`dispatch-flat-close-hint-${row.id}`} />
      ) },
    { key: "quote_number", label: "Quote", mono: true, render: (_, row) => (
        canEdit
          ? <InlineQuoteEdit item={row} onSaved={() => setRefresh((x) => x + 1)} testidPrefix="dispatch-flat-quote" />
          : (row.quote_number || <span className="t-mut">—</span>)
      ) },
    ...(tab === "awaiting_payment" ? [{
      key: "po_date", label: "PO Received", render: (_, row) => (
        <InlinePoDateEdit item={row} onSaved={() => setRefresh((x) => x + 1)} testidPrefix="dispatch-flat-po-date" />
      ),
    }] : []),
    {
      key: "invoice_number", label: "Invoice", mono: true, render: (v, row) => (
        row.status === "closed"
          ? <InlineInvoiceEdit item={row} onSaved={() => setRefresh((x) => x + 1)} testidPrefix="dispatch-flat-invoice" />
          : (v || <span className="t-mut">—</span>)
      ),
    },
    {
      key: "id", label: "", render: (_, row) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
          <button className="btn sm ghost" onClick={() => row.job_id && setOpenJob(row.job_id)} data-testid={`dispatch-view-${row.id}`}>
            <Eye size={12} /> View job
          </button>
          <button className="btn sm ghost" onClick={() => row.job_id && openCustomerFile(row.job_id)} data-testid={`dispatch-customerfile-${row.id}`}>
            <Printer size={12} /> Customer File
          </button>
          {canEdit && tab !== "closed" && (
            <button className="btn sm gold" onClick={() => row.job_id && setCloseoutJob(row.job_id)} data-testid={`dispatch-closeout-${row.id}`}>
              <ClipboardCheck size={13} /> Close-out
            </button>
          )}
          {canEdit && tab === "awaiting_payment" && (
            <button className="btn sm gold" onClick={() => setMarkingPaidRow(row)} data-testid={`mark-paid-${row.id}`}>
              <DollarSign size={12} /> Mark paid
            </button>
          )}
          {canEdit && (
            <button className="btn sm ghost" onClick={() => setInvoicingRow(row)} data-testid={`dispatch-capture-invoice-${row.id}`}>
              <DollarSign size={12} /> Capture Invoice
            </button>
          )}
          {canEdit && (
            <button className="btn sm ghost" onClick={() => setSendingBackRow(row)} data-testid={`send-back-to-lab-${row.id}`}>
              <Undo2 size={12} /> Send back to Lab
            </button>
          )}
        </div>
      ),
    },
  ];

  const emptyText = ({
    ready: "Nothing in dispatch.",
    dispatched: "Nothing ready for collection yet.",
    awaiting_payment: "Nothing handed over and awaiting payment.",
    closed: "Nothing closed yet.",
  })[tab];

  return (
    <>
      <PageHead crumb="Operations" title="Dispatch"
        sub="Close out completed jobs — print the Customer File, capture the dispatch checklist, quote/PO/invoice, payment and signoffs, then drop the item off the active list." />

      <div className="ftabs" style={{ marginBottom: 12 }}>
        {TABS.map(([k, l, Icon]) => (
          <button key={k} className={`ftab ${tab === k ? "active" : ""}`}
            onClick={() => setTab(k)}
            data-testid={`dispatch-tab-${k}`}>
            <Icon size={12} style={{ verticalAlign: "-1px", marginRight: 5 }} />{l} <span className="c" data-testid={`dispatch-tab-count-${k}`}>{fmtNum(tabCounts[k] ?? 0)}</span>
          </button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button className={`ftab ${view === "equipment" ? "active" : ""}`} onClick={() => setView("equipment")}
            data-testid="dispatch-toggle-equipment">
            <List size={12} style={{ verticalAlign: "-1px", marginRight: 5 }} />Equipment
          </button>
          <button className={`ftab ${view === "byjob" ? "active" : ""}`} onClick={() => setView("byjob")}
            data-testid="dispatch-toggle-byjob">
            <Layers size={12} style={{ verticalAlign: "-1px", marginRight: 5 }} />By Job (DN)
          </button>
        </div>
      </div>

      {tab === "awaiting_payment" && (
        <div className="card pad" style={{ display: "flex", alignItems: "center", gap: 22, marginBottom: 14, background: "#FBF3D6" }}
          data-testid="dispatch-awaiting-payment-summary">
          <span className="ico b-gold" style={{ width: 30, height: 30 }}><DollarSign size={16} /></span>
          <div>
            <div className="t-mut" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>Items</div>
            <div className="t-strong" style={{ fontSize: 20 }} data-testid="dispatch-awaiting-payment-count">{fmtNum(apSummary.count)}</div>
          </div>
          {apSummary.oldest_days != null && (
            <>
              <div style={{ borderLeft: "1px solid var(--line)", height: 32 }} />
              <div>
                <div className="t-mut" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>Oldest unpaid</div>
                <div className="t-strong" style={{ fontSize: 20 }} data-testid="dispatch-awaiting-payment-oldest">{apSummary.oldest_days}d</div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "closed" && (
        <div className="card pad" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}
          data-testid="dispatch-closed-rule-bar">
          <Archive size={15} style={{ color: "var(--gold-deep)" }} />
          <span className="t-mut" style={{ fontSize: 12.5 }}>
            A job leaves this queue once it's <b>Paid</b> and has a <b>Quote #</b>, <b>PO #</b> and <b>Invoice #</b> (type <b>N/A</b> if not applicable). The <b>To close</b> column shows what each job still needs.
          </span>
          <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap" }}
            data-testid="dispatch-show-legacy-toggle">
            <input type="checkbox" checked={showLegacy} onChange={(e) => setShowLegacy(e.target.checked)}
              data-testid="dispatch-show-legacy-checkbox" />
            Show older jobs still missing billing info
          </label>
        </div>
      )}

      {view === "byjob" ? (
        <DispatchByJobView status={tab} refreshSignal={refresh} includeLegacy={includeLegacy}
          openJob={setOpenJob} onActionApplied={() => setRefresh((x) => x + 1)} />
      ) : (
        <DataTable endpoint="/dispatch" params={{ status: tab, hide_paid: true, include_legacy: includeLegacy }} columns={columns} rowKey="id" testid="dispatch" clientSide
          defaultSort={{ by: "updated_at", dir: "desc" }} searchPlaceholder="Search DN, equipment, customer, quote #…" refreshSignal={refresh}
          emptyText={emptyText} selectable
          bulkToolbar={(ids, clear) => <DispatchBulkBar selectedIds={ids} sourceStatus={tab} clearSelection={clear} onDone={() => { clear(); setRefresh((x) => x + 1); }} />}
        />
      )}

      {closeoutJob && (
        <DispatchCloseoutModal jobId={closeoutJob}
          onClose={() => setCloseoutJob(null)}
          onDone={() => { setCloseoutJob(null); setRefresh((x) => x + 1); }} />
      )}

      {openJob && (
        <JobDetailModal jobId={openJob}
          onClose={() => setOpenJob(null)}
          onChanged={() => setRefresh((x) => x + 1)} />
      )}

      {markingPaidRow && (
        <MarkPaidModal item={markingPaidRow}
          onClose={() => setMarkingPaidRow(null)}
          onDone={() => setRefresh((x) => x + 1)} />
      )}

      {invoicingRow && (
        <InvoiceCaptureModal itemIds={[invoicingRow.id]}
          subtitle={`${invoicingRow.description || ""}${invoicingRow.serial_number ? " · SN " + invoicingRow.serial_number : ""} · DN ${invoicingRow.delivery_note_number || ""}`}
          onClose={() => setInvoicingRow(null)}
          onDone={() => setRefresh((x) => x + 1)} />
      )}

      {sendingBackRow && (
        <SendBackToLabModal item={sendingBackRow}
          onClose={() => setSendingBackRow(null)}
          onDone={() => setRefresh((x) => x + 1)} />
      )}
    </>
  );
}
